import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "@tanstack/react-router";
import { toast } from "sonner";
import { useInventoryFilter } from "@/lib/inventory-filter";
import { useScopedNavigate } from "@/lib/scoped-nav";
import {
  encodeInventoryScope,
  inventorySearchKey,
} from "@/lib/inventory-scope-codec";
import {
  cardBase,
  cardSubtle,
  cardToneDot,
  cardTonePill,
  cardToneText,
  moduleCard,
  moduleCardPaused,
} from "@/lib/card-styles";
import { CardGlowOverlay } from "@/components/ui/card-glow-overlay";
import { cn } from "@/lib/utils";
import {
  Activity,
  ArrowRight,
  BrainCircuit,
  CheckCircle2,
  ChevronRight,
  FastForward,
  FlaskConical,
  Loader2,
  Pause,
  Play,
  X,
  Wrench,
  XCircle,
  Zap,
} from "lucide-react";

import {
  listAgents,
  listRuns,
  setAgentScheduleEnablement,
  triggerAgent,
  type AgentDefinition,
  type AgentRun,
} from "@/lib/api/agent";
import {
  loadActivityFeed,
  type NormalizedActivityRow,
} from "@/components/control-tower/activity-feed";
import { subline } from "@/components/control-tower/activity-feed-filters";
import {
  AGENT_RUN_WAIT_MS,
  AGENT_TRIGGER_ACCEPT_MS,
  awaitWithDeadline,
  pollAgentRunUntilDone,
  runButtonAfterTriggerPost,
} from "./agent-run-poll";
import { SectionPanel } from "@/components/ui/section-panel";
import {
  agent_scope_status,
  filter_activity_for_agent,
  latest_activity_when,
  produced_work_nav,
  schedule_card_tone,
  schedule_control_state,
  schedule_status_label,
  scope_building_ids,
  trigger_visible,
  type AgentScopeStatus,
  type ScheduleControlState,
  type ScopeLabel,
} from "./agent-team-panel-filters";

const ICON: Record<AgentDefinition["icon_type"], React.ElementType> = {
  energy: Zap,
  analysis: Activity,
  strategy: BrainCircuit,
  config: Wrench,
  simulation: FlaskConical,
};

const ICON_COLOR: Record<AgentDefinition["icon_type"], string> = {
  energy: "text-warning-subtle-foreground",
  analysis: "text-blue-400",
  strategy: "text-primary-subtle-foreground",
  config: "text-success-subtle-foreground",
  simulation: "text-cyan-400",
};

function format_last_run(ts: string | null): string {
  if (!ts) return "Ei vielä ajettu";
  const diff_ms = Date.now() - new Date(ts).getTime();
  const hours = Math.floor(diff_ms / 3_600_000);
  if (hours < 1) return "Alle tunti sitten";
  if (hours < 24) return `${hours} t sitten`;
  const days = Math.floor(hours / 24);
  return `${days} pv sitten`;
}

function status_label(status: AgentScopeStatus): string {
  if (status.kind === "loading") return "…";
  if (status.kind === "empty")
    return `Ei tapahtumia tässä ${status.scopeLabel}`;
  return format_last_run(status.lastRunAt);
}

type RunState = "idle" | "running" | "done" | "error";

// Kiinteistötiimi — the whole shipped `_REGISTERED_AGENTS` roster, always visible at every
// inventory scope (kiinteistökanta / salkku / building). A card's status narrows to the current
// scope (agent_scope_status); selecting a card expands its scoped Tapahtumaloki below the grid.
export function AgentTeamPanel() {
  const { filter, groupId, buildingId, activeGroup } = useInventoryFilter();
  const [agents, setAgents] = useState<AgentDefinition[]>([]);
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [scopedRuns, setScopedRuns] = useState<Record<string, AgentRun[]>>({});

  function refresh() {
    listAgents(buildingId ?? undefined)
      .then(setAgents)
      .catch(() => null);
  }

  // Re-fetches on every building change, not just on mount — each agent's
  // `enabled_for_building` is scoped to the *selected* building (story #1604), so switching
  // buildings (or clearing the selection back to kiinteistökanta/salkku) must re-read it.
  useEffect(() => {
    refresh();
    // buildingId is the stable primitive; refresh is a fresh closure every render (reads
    // buildingId itself), same pattern as the scopedRuns/activityRows effects below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId]);

  // The server response from a schedule-enablement write is the only source of truth for
  // "paused" — this merges it into the roster `listAgents` already populated, rather than a
  // second piece of state a card could show instead of the server value.
  function handle_schedule_changed(agentId: string, enabled: boolean) {
    setAgents((prev) =>
      prev.map((a) =>
        a.id === agentId ? { ...a, enabled_for_building: enabled } : a,
      ),
    );
  }

  const scopeBuildingIds = scope_building_ids(
    buildingId,
    activeGroup?.building_ids ?? null,
  );
  const scopeLabel: ScopeLabel = buildingId ? "kiinteistössä" : "salkussa";

  // A card's status at salkku/building scope needs each agent's runs to find the latest one
  // touching the scoped buildings — kiinteistökanta scope skips this fetch entirely and reads
  // the agent's own (already-aggregate) last_run_at instead. One GET per roster agent (no batch
  // "runs for these agents" endpoint exists yet) — acceptable at today's roster size (6 agents).
  useEffect(() => {
    if (scopeBuildingIds === null || agents.length === 0) {
      setScopedRuns({});
      return;
    }
    let cancelled = false;
    Promise.all(
      agents.map((a) =>
        listRuns(a.id)
          .then((runs) => [a.id, runs] as const)
          .catch(() => [a.id, []] as const),
      ),
    ).then((pairs) => {
      if (!cancelled) setScopedRuns(Object.fromEntries(pairs));
    });
    return () => {
      cancelled = true;
    };
    // buildingId/activeGroup?.id are the stable primitives scopeBuildingIds is derived from —
    // it is a fresh array every render otherwise.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId, activeGroup?.id, agents]);

  // The scoped Tapahtumaloki feed is fetched once here (keyed by scope) and shared by whichever
  // card's detail panel is open, rather than refetching the whole feed on every card selection.
  const scope = encodeInventoryScope({ filter, groupId, buildingId });
  const scopeSearchKey = inventorySearchKey(scope);
  const [activityRows, setActivityRows] = useState<NormalizedActivityRow[]>([]);
  const [activityLoading, setActivityLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setActivityLoading(true);
    loadActivityFeed(scope, {})
      .then((rows) => {
        if (!cancelled) setActivityRows(rows);
      })
      .catch(() => {
        if (!cancelled) setActivityRows([]);
      })
      .finally(() => {
        if (!cancelled) setActivityLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // scopeSearchKey is the stable primitive; scope is a fresh object every render
    // (encodeInventoryScope), same as ActivityFeed (#1251).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeSearchKey]);

  // Every card's status is read from this same map — computed once per render instead of once
  // per card *and* again for whichever card's detail panel is open.
  const statusByAgentId = useMemo(
    () =>
      Object.fromEntries(
        agents.map((agent) => [
          agent.id,
          activityLoading
            ? ({ kind: "loading" } satisfies AgentScopeStatus)
            : agent_scope_status(
                agent,
                scopeBuildingIds,
                scopeBuildingIds === null ? undefined : scopedRuns[agent.id],
                scopeLabel,
                latest_activity_when(activityRows, agent.display_name),
              ),
        ]),
      ),
    [
      agents,
      scopeBuildingIds,
      scopedRuns,
      scopeLabel,
      activityLoading,
      activityRows,
    ],
  );

  if (agents.length === 0) return null;

  const selectedAgent = agents.find((a) => a.id === selectedAgentId) ?? null;

  function handle_select(agentId: string) {
    setSelectedAgentId((prev) => (prev === agentId ? null : agentId));
  }

  return (
    <SectionPanel icon={BrainCircuit} title="Kiinteistötiimi" className="mt-6">
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {agents.map((agent) => (
            <AgentCard
              key={agent.id}
              agent={agent}
              status={statusByAgentId[agent.id]}
              selected={agent.id === selectedAgentId}
              buildingId={buildingId}
              onSelect={() => handle_select(agent.id)}
              onRunComplete={refresh}
              onScheduleChanged={handle_schedule_changed}
            />
          ))}
        </div>

        {selectedAgent && (
          <AgentDetailView
            agent={selectedAgent}
            status={statusByAgentId[selectedAgent.id]}
            rows={filter_activity_for_agent(
              activityRows,
              selectedAgent.display_name,
            )}
            loading={activityLoading}
            showBuildingName={!buildingId}
            onClose={() => setSelectedAgentId(null)}
          />
        )}
      </div>
    </SectionPanel>
  );
}

function AgentCard({
  agent,
  status,
  selected,
  buildingId,
  onSelect,
  onRunComplete,
  onScheduleChanged,
}: {
  agent: AgentDefinition;
  status: AgentScopeStatus;
  selected: boolean;
  buildingId: string | null;
  onSelect: () => void;
  onRunComplete: () => void;
  onScheduleChanged: (agentId: string, enabled: boolean) => void;
}) {
  const Icon = ICON[agent.icon_type];
  const icon_color = ICON_COLOR[agent.icon_type];
  const navigate = useScopedNavigate();
  const [run_state, set_run_state] = useState<RunState>("idle");

  // A run poll can outlive the card (navigate away). Cleared on unmount so we
  // never set_run_state after a real unmount. The effect must set `true` on
  // run — React StrictMode's dev double-invoke would otherwise leave this
  // false forever after the simulated cleanup, and "Käynnissä…" would stick.
  const mounted_ref = useRef(true);
  useEffect(() => {
    mounted_ref.current = true;
    return () => {
      mounted_ref.current = false;
    };
  }, []);

  function reset_to_idle_soon() {
    setTimeout(() => {
      if (mounted_ref.current) set_run_state("idle");
    }, 4000);
  }

  async function handle_run(e: React.MouseEvent) {
    e.stopPropagation();
    // "Aja nyt" only ever renders once a building is selected (trigger_visible) — this guard
    // is belt-and-suspenders so a stray call can never reach `triggerAgent` without one.
    if (run_state === "running" || buildingId === null) return;
    set_run_state("running");
    let run: AgentRun;
    try {
      run = await awaitWithDeadline(
        triggerAgent(agent.id, buildingId),
        AGENT_TRIGGER_ACCEPT_MS,
      );
    } catch {
      if (mounted_ref.current) {
        set_run_state(runButtonAfterTriggerPost(false));
        reset_to_idle_soon();
      }
      return;
    }
    if (!mounted_ref.current) return;

    // 202 Accepted: the worker has the run. Do not hold "Käynnissä…" until
    // GET /runs reports completed — that GET shares the agent process with the
    // KB tick POST just started, so it will not return until the tick finishes.
    set_run_state(runButtonAfterTriggerPost(true));
    reset_to_idle_soon();

    void pollAgentRunUntilDone({
      runId: run.id,
      agentId: agent.id,
      listRuns,
      isCancelled: () => !mounted_ref.current,
      timeoutMs: AGENT_RUN_WAIT_MS,
    }).then((outcome) => {
      if (!mounted_ref.current || outcome !== "completed") return;
      onRunComplete();
    });
  }

  // "Aja nyt" is a one-shot run outcome (idle/running/done/error); the schedule toggle below is
  // a persisted on/off pair — two separate concepts, never merged into one label or one control.
  const footer_label =
    run_state === "running"
      ? "Käynnissä…"
      : run_state === "error"
        ? "Virhe"
        : status_label(status);

  const nav = produced_work_nav(agent.id);
  const schedule_state = schedule_control_state(
    buildingId,
    agent.enabled_for_building,
  );
  const schedule_tone = schedule_card_tone(schedule_state);
  const roster_dot_tone =
    schedule_state.kind === "hidden"
      ? agent.is_active
        ? "ok"
        : "empty"
      : schedule_tone;
  const paused = schedule_state.kind === "resolved" && !schedule_state.enabled;

  return (
    <div
      className={cn(
        moduleCard,
        "p-4 flex flex-col gap-3",
        paused && moduleCardPaused,
        selected
          ? "border-primary ring-2 ring-primary ring-offset-2 ring-offset-background"
          : "",
      )}
      onClick={onSelect}
    >
      <CardGlowOverlay />
      <div className="relative flex items-start justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <div
            className={cn(
              "rounded-lg bg-muted p-2 shrink-0",
              paused && "opacity-50",
            )}
          >
            <Icon className={`h-4 w-4 ${icon_color}`} />
          </div>
          <div className="min-w-0">
            <p
              className={cn(
                "text-sm font-semibold leading-tight truncate",
                paused && "text-muted-foreground",
              )}
            >
              {agent.display_name}
            </p>
            {schedule_state.kind === "hidden" ? (
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {agent.is_active ? "Aktiivinen" : "Ei käytössä"}
              </p>
            ) : (
              <span
                className={cn(
                  "mt-1 inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium",
                  cardTonePill[schedule_tone],
                )}
              >
                {schedule_status_label(schedule_state)}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {buildingId !== null && schedule_state.kind !== "hidden" && (
            <ScheduleToggleButton
              agentId={agent.id}
              agentDisplayName={agent.display_name}
              buildingId={buildingId}
              state={schedule_state}
              onChanged={(enabled) => onScheduleChanged(agent.id, enabled)}
            />
          )}
          {trigger_visible(buildingId) && (
            <RunButton state={run_state} onClick={handle_run} />
          )}
          <span
            className={cn(
              "rounded-full w-2 h-2 mt-0.5",
              cardToneDot[roster_dot_tone],
            )}
          />
        </div>
      </div>

      <p
        className={cn(
          "relative text-xs text-muted-foreground leading-relaxed",
          paused && "opacity-60",
        )}
      >
        {agent.description_fi}
      </p>

      <div
        className={cn(
          "relative flex flex-wrap gap-1.5",
          paused && "opacity-60",
        )}
      >
        {agent.capability_labels.map((label) => (
          <span
            key={label}
            className="rounded-md bg-muted px-2 py-0.5 text-[10px] text-muted-foreground font-medium"
          >
            {label}
          </span>
        ))}
      </div>

      <div className="relative mt-auto space-y-1.5">
        <p className="text-[10px] text-muted-foreground/60">{footer_label}</p>
        {nav && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              void navigate({ to: nav.route });
            }}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
          >
            {nav.label}
            <ArrowRight className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
}

// Play/Pause — one control, two states, for the persisted per-building schedule (story #1604).
// Adapted from LiveOptimizationToggle's server-backed toggle (local `saving` flag + sonner toast
// + write through `request()`), applied inline on the card instead of a SectionPanel. The
// server's response is the only value ever written back (`onChanged`) — never an optimistic
// guess — so a reload always shows what this call actually persisted.
function ScheduleToggleButton({
  agentId,
  agentDisplayName,
  buildingId,
  state,
  onChanged,
}: {
  agentId: string;
  agentDisplayName: string;
  buildingId: string;
  state: ScheduleControlState;
  onChanged: (enabled: boolean) => void;
}) {
  const [saving, setSaving] = useState(false);

  async function toggle(e: React.MouseEvent) {
    e.stopPropagation();
    if (state.kind !== "resolved" || saving) return;
    const next = !state.enabled;
    setSaving(true);
    try {
      const result = await setAgentScheduleEnablement(
        agentId,
        buildingId,
        next,
      );
      onChanged(result.enabled);
      toast.success(
        result.enabled
          ? `${agentDisplayName} käynnissä`
          : `${agentDisplayName} pysäytetty`,
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Päivitys epäonnistui");
    } finally {
      setSaving(false);
    }
  }

  const resolved = state.kind === "resolved";
  const running = resolved && state.enabled;
  const loading = saving || state.kind === "loading";

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={!resolved || saving}
      title={running ? "Pysäytä ajastus" : "Käynnistä ajastus"}
      className={cn(
        "rounded p-0.5 transition-colors disabled:opacity-50",
        running
          ? cn(cardToneText.ok, "hover:bg-success/10")
          : cn(cardToneText.warn, "hover:bg-warning/10"),
      )}
    >
      {loading ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : running ? (
        <Pause className="h-3.5 w-3.5" />
      ) : (
        <Play className="h-3.5 w-3.5" />
      )}
    </button>
  );
}

function RunButton({
  state,
  onClick,
}: {
  state: RunState;
  onClick: (e: React.MouseEvent) => void;
}) {
  if (state === "running") {
    return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
  }
  if (state === "done") {
    return <CheckCircle2 className="h-4 w-4 text-success-subtle-foreground" />;
  }
  if (state === "error") {
    return <XCircle className="h-4 w-4 text-destructive" />;
  }
  return (
    <button
      onClick={onClick}
      className="rounded p-0.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
      title="Aja nyt"
    >
      <FastForward className="h-3.5 w-3.5" />
    </button>
  );
}

function AgentDetailView({
  agent,
  status,
  rows,
  loading,
  showBuildingName,
  onClose,
}: {
  agent: AgentDefinition;
  status: AgentScopeStatus;
  rows: NormalizedActivityRow[];
  loading: boolean;
  showBuildingName: boolean;
  onClose: () => void;
}) {
  const Icon = ICON[agent.icon_type];
  const icon_color = ICON_COLOR[agent.icon_type];
  const router = useRouter();

  const now = Date.now();

  return (
    <div className={cn("mt-4", cardBase, "p-5")}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5">
          <div className="rounded-lg bg-muted p-2 shrink-0">
            <Icon className={`h-4 w-4 ${icon_color}`} />
          </div>
          <div>
            <p className="text-sm font-semibold leading-tight">
              {agent.display_name}
            </p>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              Tapahtumaloki
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="rounded p-1 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          title="Sulje"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          <span>Ladataan tapahtumia…</span>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <ul className="max-h-56 space-y-1.5 overflow-y-auto">
          {rows.slice(0, 8).map((row, i) => (
            <li key={`${row.kind}-${row.where.id}-${row.when}-${i}`}>
              <button
                type="button"
                onClick={() => void router.navigate({ href: row.link })}
                className={cn(
                  cardSubtle,
                  "w-full flex items-center justify-between gap-2 p-2.5 text-left transition-colors hover:bg-surface/60",
                )}
              >
                <span className="min-w-0">
                  <span className="block truncate text-xs text-foreground">
                    {row.summary}
                  </span>
                  <span className="block truncate text-[10.5px] text-muted-foreground mt-0.5">
                    {subline(row, now, null, showBuildingName)}
                  </span>
                </span>
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {!loading && rows.length === 0 && (
        <p className="text-xs text-muted-foreground italic">
          {status.kind === "empty"
            ? `Ei tapahtumia lokissa tässä ${status.scopeLabel}.`
            : status.kind === "aggregate" && status.lastRunAt
              ? `Ei vielä tapahtumia lokissa — viimeisin ajo ${format_last_run(status.lastRunAt)}.`
              : "Ei vielä tuloksia — aja agentti ensimmäistä kertaa."}
        </p>
      )}
    </div>
  );
}
